// DESCRIPTION: Shared per-event visual styling editor — template picker, 13
// DESCRIPTION: token controls (6 colors + 7 enums), and a live EventHero preview
// DESCRIPTION: rendered in a sticky sidecar. Used by both the wizard StylingStep
// DESCRIPTION: and the main EventForm "Event Design" card.
import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import {
	ColorPicker,
	Dropdown,
	Button,
	SelectControl,
} from '@wordpress/components';
import EventHero from '../../components/booking/EventHero';
import { getEventStyleVars } from '../../frontend/utils/eventStyling';

const RADIUS_OPTIONS = [
	{ label: __( 'Default (theme)', 'nemtly-booking' ), value: '' },
	{ label: __( 'Sharp', 'nemtly-booking' ), value: 'sharp' },
	{ label: __( 'Rounded', 'nemtly-booking' ), value: 'rounded' },
];

const SHADOW_OPTIONS = [
	{ label: __( 'Default (theme)', 'nemtly-booking' ), value: '' },
	{ label: __( 'None', 'nemtly-booking' ), value: 'none' },
	{ label: __( 'Low', 'nemtly-booking' ), value: 'low' },
	{ label: __( 'Medium', 'nemtly-booking' ), value: 'medium' },
	{ label: __( 'High', 'nemtly-booking' ), value: 'high' },
];

const SPACING_OPTIONS = [
	{ label: __( 'Default (theme)', 'nemtly-booking' ), value: '' },
	{ label: __( 'Compact', 'nemtly-booking' ), value: 'compact' },
	{ label: __( 'Normal', 'nemtly-booking' ), value: 'normal' },
	{ label: __( 'Loose', 'nemtly-booking' ), value: 'loose' },
];

const HEADING_SIZE_OPTIONS = [
	{ label: __( 'Default (theme)', 'nemtly-booking' ), value: '' },
	{ label: __( 'Small', 'nemtly-booking' ), value: 'sm' },
	{ label: __( 'Medium', 'nemtly-booking' ), value: 'md' },
	{ label: __( 'Large', 'nemtly-booking' ), value: 'lg' },
];

const HEADING_WEIGHT_OPTIONS = [
	{ label: __( 'Default (theme)', 'nemtly-booking' ), value: '' },
	{ label: __( 'Normal', 'nemtly-booking' ), value: 'normal' },
	{ label: __( 'Medium', 'nemtly-booking' ), value: 'medium' },
	{ label: __( 'Semibold', 'nemtly-booking' ), value: 'semibold' },
	{ label: __( 'Bold', 'nemtly-booking' ), value: 'bold' },
];

/**
 * Color token fields. Each entry wires one `styling.*` key to a plain-English
 * label + help line. The key is the data-layer token name (see
 * Event_Templates::COLOR_KEYS); the label/help is what the admin sees.
 */
const COLOR_FIELDS = [
	{
		key: 'header_background',
		label: __( 'Header background', 'nemtly-booking' ),
		help: __(
			'The colored banner across the top of your event card. Also tints the banner image when one is set.',
			'nemtly-booking'
		),
	},
	{
		key: 'header_title_text',
		label: __( 'Header title text', 'nemtly-booking' ),
		help: __(
			'The event title shown in the header band.',
			'nemtly-booking'
		),
	},
	{
		key: 'tag_text',
		label: __( 'Tag text', 'nemtly-booking' ),
		help: __(
			'The label on the small pill (e.g. the event type).',
			'nemtly-booking'
		),
	},
	{
		key: 'tag_background',
		label: __( 'Tag background', 'nemtly-booking' ),
		help: __( 'The background of the small pill.', 'nemtly-booking' ),
	},
	{
		key: 'detail_text',
		label: __( 'Date, time & detail text', 'nemtly-booking' ),
		help: __(
			'The smaller text for the date, time, and location.',
			'nemtly-booking'
		),
	},
	{
		key: 'price_text',
		label: __( 'Price text', 'nemtly-booking' ),
		help: __( 'The event price.', 'nemtly-booking' ),
	},
	{
		key: 'button_background',
		label: __( 'Button background', 'nemtly-booking' ),
		help: __( 'The "Book now" button background.', 'nemtly-booking' ),
	},
	{
		key: 'button_text',
		label: __( 'Button text', 'nemtly-booking' ),
		help: __( 'The "Book now" button label.', 'nemtly-booking' ),
	},
	{
		key: 'card_background',
		label: __( 'Card background', 'nemtly-booking' ),
		help: __(
			'The background of the event card and any form fields inside it.',
			'nemtly-booking'
		),
	},
	{
		key: 'border_color',
		label: __( 'Borders & dividers', 'nemtly-booking' ),
		help: __(
			'The outline around the card and the lines that separate sections.',
			'nemtly-booking'
		),
	},
];

/**
 * Enum-based token fields, grouped by section. Each entry pairs a `styling.*`
 * key with its label and the option list for the dropdown.
 */
const SHAPE_FIELDS = [
	{
		key: 'card_radius',
		label: __( 'Card radius', 'nemtly-booking' ),
		options: RADIUS_OPTIONS,
	},
	{
		key: 'button_radius',
		label: __( 'Button radius', 'nemtly-booking' ),
		options: RADIUS_OPTIONS,
	},
	{
		key: 'shadow',
		label: __( 'Shadow', 'nemtly-booking' ),
		options: SHADOW_OPTIONS,
	},
];

const TYPOGRAPHY_FIELDS = [
	{
		key: 'spacing',
		label: __( 'Spacing', 'nemtly-booking' ),
		options: SPACING_OPTIONS,
	},
	{
		key: 'heading_size',
		label: __( 'Heading size', 'nemtly-booking' ),
		options: HEADING_SIZE_OPTIONS,
	},
	{
		key: 'heading_weight',
		label: __( 'Heading weight', 'nemtly-booking' ),
		options: HEADING_WEIGHT_OPTIONS,
	},
];

/**
 * A single color token field: swatch + popover color picker + reset.
 *
 * @param {Object}   props
 * @param {string}   props.label    Field label.
 * @param {string}   props.help     Helper text.
 * @param {string}   props.value    Current hex color, or '' to inherit.
 * @param {Function} props.onChange Called with the new hex color (or '' on reset).
 */
const ColorField = ( { label, help, value, onChange } ) => {
	return (
		<div className="nb-flex nb-items-center nb-justify-between nb-gap-4 nb-py-2">
			<div>
				<div className="nb-text-sm nb-font-semibold nb-text-neutral-700">
					{ label }
				</div>
				{ help && (
					<div className="nb-text-xs nb-text-neutral-500">
						{ help }
					</div>
				) }
			</div>
			<div className="nb-flex nb-items-center nb-gap-2 nb-flex-none">
				{ value && (
					<Button
						variant="link"
						className="nb-text-xs"
						onClick={ () => onChange( '' ) }
					>
						{ __( 'Reset', 'nemtly-booking' ) }
					</Button>
				) }
				<Dropdown
					popoverProps={ { placement: 'bottom-end' } }
					renderToggle={ ( { isOpen, onToggle } ) => (
						<button
							type="button"
							onClick={ onToggle }
							aria-expanded={ isOpen }
							aria-label={ label }
							className="nb-w-8 nb-h-8 nb-rounded-full nb-border nb-border-neutral-300 nb-cursor-pointer nb-p-0 nb-shrink-0"
							style={ {
								backgroundColor: value || 'transparent',
								backgroundImage: value
									? 'none'
									: 'linear-gradient(45deg, var(--nb-checker, #e5e5e5) 25%, transparent 25%, transparent 75%, var(--nb-checker, #e5e5e5) 75%), linear-gradient(45deg, var(--nb-checker, #e5e5e5) 25%, transparent 25%, transparent 75%, var(--nb-checker, #e5e5e5) 75%)',
								backgroundSize: '8px 8px',
								backgroundPosition: '0 0, 4px 4px',
							} }
						/>
					) }
					renderContent={ () => (
						<ColorPicker
							color={ value || undefined }
							onChange={ onChange }
							enableAlpha={ false }
							defaultValue="#000000"
						/>
					) }
				/>
			</div>
		</div>
	);
};

/**
 * Build a synthetic event object from the form data, for the EventHero preview.
 *
 * @param {Object} formData The event form data.
 *
 * @return {Object} Synthetic event object shaped like the REST API response.
 */
const buildPreviewEvent = ( formData ) => ( {
	title: {
		rendered: formData.title || __( 'Your Event Title', 'nemtly-booking' ),
	},
	description: formData.content || '',
	event_type: formData.event_type || 'one-on-one',
	date: formData.event_date || '',
	time: formData.event_time || '',
	location: formData.location || '',
	duration: formData.event_duration || 60,
	price: formData.price || 0,
	currency_symbol: '$',
	featured_image: formData.featured_image || '',
} );

/**
 * Shared per-event styling editor.
 *
 * Renders a template picker, token controls, and a live EventHero preview.
 * Used by both the wizard's StylingStep and the main EventForm "Event Design"
 * card — one component, one source of truth, no mock to drift from.
 *
 * @param {Object}   props
 * @param {Object}   props.styling   Current styling tokens.
 * @param {Function} props.onChange  Called with the new styling object.
 * @param {Object}   props.formData  Event form data (for the preview).
 * @param {Object}   props.templates Template catalog from nemtlyCreateEvent.
 */
const EventStylingEditor = ( { styling, onChange, formData, templates } ) => {
	const setToken = ( key, value ) => {
		onChange( { ...styling, [ key ]: value } );
	};

	const applyTemplate = ( templateId ) => {
		const template = templates && templates[ templateId ];
		if ( ! template ) {
			return;
		}
		onChange( { template: templateId, ...template.tokens } );
	};

	const clearAll = () => {
		onChange( { template: '' } );
	};

	const previewEvent = buildPreviewEvent( formData );
	const previewVars = getEventStyleVars( { styling } );
	const catalog = templates || {};

	// Live-preview controls: device frame + event-type-aware booking surface.
	const [ device, setDevice ] = useState( 'desktop' );
	const isSlotBased =
		formData.event_type === 'one-on-one' || formData.event_type === 'group';
	const previewMaxWidth = device === 'mobile' ? 380 : undefined;

	return (
		<div className="nb-grid nb-gap-8 lg:nb-grid-cols-[1fr_400px] nb-items-start">
			{ /* Controls column */ }
			<div>
				<p className="nb-text-sm nb-text-neutral-500 nb-mb-6">
					{ __(
						'Pick a template to start, then customize it your way!',
						'nemtly-booking'
					) }
				</p>

				{ /* Template picker */ }
				{ Object.keys( catalog ).length > 0 && (
					<div className="nb-mb-8">
						<label className="nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-2">
							{ __( 'Template', 'nemtly-booking' ) }
						</label>
						<div className="nb-grid nb-grid-cols-3 nb-gap-2 sm:nb-grid-cols-6 lg:nb-grid-cols-3 xl:nb-grid-cols-6">
							{ Object.entries( catalog ).map(
								( [ id, template ] ) => {
									const isActive = styling.template === id;
									return (
										<button
											key={ id }
											type="button"
											onClick={ () =>
												applyTemplate( id )
											}
											className={ `nb-flex nb-flex-col nb-items-center nb-gap-2 nb-p-3 nb-rounded-lg nb-border nb-bg-neutral-0 nb-text-center nb-transition-all nb-cursor-pointer ${
												isActive
													? 'nb-border-accent-500 nb-shadow-[0_0_0_1px_#252525]'
													: 'nb-border-neutral-300 hover:nb-border-neutral-500'
											}` }
										>
											<span
												className="nb-flex nb-items-center"
												aria-hidden="true"
											>
												<span
													className="nb-w-5 nb-h-5 nb-rounded-full nb-shadow-[0_0_0_2px_#fff]"
													style={ {
														background:
															template.tokens
																.header_background ||
															'#D6D4CD',
													} }
												/>
												<span
													className="-nb-ml-1.5 nb-w-5 nb-h-5 nb-rounded-full nb-shadow-[0_0_0_2px_#fff,inset_0_0_0_1px_#D6D4CD]"
													style={ {
														background:
															template.tokens
																.card_background ||
															'#FFFFFF',
													} }
												/>
												<span
													className="-nb-ml-1.5 nb-w-5 nb-h-5 nb-rounded-full nb-shadow-[0_0_0_2px_#fff]"
													style={ {
														background:
															template.tokens
																.price_text ||
															'#0F0F10',
													} }
												/>
											</span>
											<span className="nb-flex nb-items-center nb-gap-1 nb-text-xs nb-font-semibold nb-text-neutral-900">
												{ isActive && (
													<span
														className="nb-text-success-500"
														aria-hidden="true"
													>
														&#x2713;
													</span>
												) }
												{ template.label }
											</span>
										</button>
									);
								}
							) }
						</div>
						{ styling.template && (
							<Button
								variant="link"
								className="nb-text-sm nb-mt-2"
								onClick={ clearAll }
							>
								{ __( 'Clear all styling', 'nemtly-booking' ) }
							</Button>
						) }
					</div>
				) }

				{ /* Color tokens */ }
				<div className="nb-mb-6">
					<h4 className="nb-text-sm nb-font-semibold nb-text-neutral-900 nb-mb-3">
						{ __( 'Colors', 'nemtly-booking' ) }
					</h4>
					{ COLOR_FIELDS.filter(
						( { key } ) =>
							! (
								key === 'header_background' &&
								formData.featured_image
							)
					).map( ( { key, label, help } ) => (
						<ColorField
							key={ key }
							label={ label }
							help={ help }
							value={ styling[ key ] || '' }
							onChange={ ( v ) => setToken( key, v ) }
						/>
					) ) }
				</div>

				{ /* Shape tokens */ }
				<div className="nb-mb-6">
					<h4 className="nb-text-sm nb-font-semibold nb-text-neutral-900 nb-mb-3">
						{ __( 'Shape', 'nemtly-booking' ) }
					</h4>
					{ SHAPE_FIELDS.map( ( { key, label, options } ) => (
						<SelectControl
							key={ key }
							label={ label }
							value={ styling[ key ] || '' }
							options={ options }
							onChange={ ( v ) => setToken( key, v ) }
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					) ) }
				</div>

				{ /* Spacing + Typography */ }
				<div>
					<h4 className="nb-text-sm nb-font-semibold nb-text-neutral-900 nb-mb-3">
						{ __( 'Typography & Spacing', 'nemtly-booking' ) }
					</h4>
					{ TYPOGRAPHY_FIELDS.map(
						( { key, label, options, help } ) => (
							<SelectControl
								key={ key }
								label={ label }
								value={ styling[ key ] || '' }
								options={ options }
								onChange={ ( v ) => setToken( key, v ) }
								help={ help }
								__next40pxDefaultSize
								__nextHasNoMarginBottom
							/>
						)
					) }
				</div>
			</div>

			{ /* Sticky preview sidecar */ }
			<div className="lg:nb-sticky lg:nb-top-[120px]">
				<div className="nb-flex nb-items-center nb-justify-between nb-mb-2">
					<label className="nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-m-0">
						{ __( 'Live preview', 'nemtly-booking' ) }
					</label>
					<div
						className="nb-flex nb-items-center nb-gap-1 nb-p-0.5 nb-bg-neutral-50 nb-border nb-border-neutral-200 nb-rounded-md"
						role="group"
						aria-label={ __( 'Preview device', 'nemtly-booking' ) }
					>
						<button
							type="button"
							className={ `nb-px-2 nb-py-0.5 nb-text-xs nb-font-medium nb-rounded nb-cursor-pointer nb-border-0 nb-transition-colors ${
								device === 'desktop'
									? 'nb-bg-neutral-0 nb-text-neutral-900 nb-shadow-elevation-1'
									: 'nb-bg-transparent nb-text-neutral-500'
							}` }
							aria-pressed={ device === 'desktop' }
							onClick={ () => setDevice( 'desktop' ) }
						>
							{ __( 'Desktop', 'nemtly-booking' ) }
						</button>
						<button
							type="button"
							className={ `nb-px-2 nb-py-0.5 nb-text-xs nb-font-medium nb-rounded nb-cursor-pointer nb-border-0 nb-transition-colors ${
								device === 'mobile'
									? 'nb-bg-neutral-0 nb-text-neutral-900 nb-shadow-elevation-1'
									: 'nb-bg-transparent nb-text-neutral-500'
							}` }
							aria-pressed={ device === 'mobile' }
							onClick={ () => setDevice( 'mobile' ) }
						>
							{ __( 'Mobile', 'nemtly-booking' ) }
						</button>
					</div>
				</div>
				<div
					className="nb-rounded-lg nb-border nb-border-neutral-200 nb-bg-neutral-50 nb-p-4"
					style={ { containerType: 'inline-size' } }
				>
					<div
						className="nb-event-hero-preview nb-mx-auto nb-transition-all"
						style={ {
							maxWidth: previewMaxWidth,
							containerType: 'inline-size',
						} }
					>
						<EventHero
							event={ previewEvent }
							slotBased={ isSlotBased }
							onBook={ () => {} }
							style={ previewVars }
						/>
					</div>
				</div>
				<p className="nb-text-xs nb-text-neutral-500 nb-mt-2">
					{ __(
						'Preview reflects your title, schedule, banner, and styling. The full booking flow opens when customers click "Book now".',
						'nemtly-booking'
					) }
				</p>
			</div>
		</div>
	);
};

export default EventStylingEditor;
